import { ApiRoutes, AppPaginationOptions, QueryError, WithPagination } from '@/common/interfaces';
import { apiExecutor, buildMutation, buildQuery } from '@/common/services';

import { SearchConsoleAnalyticsRequestData } from '@/modules/Domains';
import { SiteGroup, SiteGroupFormFields, SiteGroupSearchAnalytics, SiteGroupsQueryKeys } from '@/modules/SiteGroups';
import { Site } from '@/modules/Sites';

export const useGetAllSiteGroupsQuery = (paginationOptions: AppPaginationOptions, search?: string) =>
  buildQuery<WithPagination<SiteGroup>, QueryError>([SiteGroupsQueryKeys.GetAll, { ...paginationOptions, search }], async () => {
    const { data } = await apiExecutor.get<WithPagination<SiteGroup>>(ApiRoutes.SiteGroups, {
      params: { ...paginationOptions, search },
    });
    return data;
  });

export const useGetOneByIdSiteGroupsQuery = (id: string) =>
  buildQuery<SiteGroup, QueryError>([SiteGroupsQueryKeys.GetOneById, id], async () => {
    const { data } = await apiExecutor.get<SiteGroup>(ApiRoutes.SiteGroupsById.replace(':siteGroupId', id));
    return data;
  });

export const useCreateSiteGroupsMutation = () =>
  buildMutation<SiteGroup, QueryError, SiteGroupFormFields>([SiteGroupsQueryKeys.Create], async (postData) => {
    const { data } = await apiExecutor.post<SiteGroup>(ApiRoutes.SiteGroups, postData);
    return data;
  });

export const useUpdateSiteGroupsMutation = (id: string) =>
  buildMutation<SiteGroup, QueryError, Partial<SiteGroupFormFields>>([SiteGroupsQueryKeys.Edit, id], async (postData) => {
    const { data } = await apiExecutor.patch<SiteGroup>(ApiRoutes.SiteGroupsById.replace(':siteGroupId', id), postData);
    return data;
  });

export const useDeleteSiteGroupsMutation = (id: string) =>
  buildMutation<void, QueryError, void>([SiteGroupsQueryKeys.Delete, id], async () => {
    await apiExecutor.delete<void>(ApiRoutes.SiteGroupsById.replace(':siteGroupId', id));
  });

export const useBreadcrumbSiteGroupsQuery = (id: string) =>
  buildQuery<string, QueryError>([SiteGroupsQueryKeys.Breadcrumb, id], async () => {
    const { data } = await apiExecutor.get<string>(ApiRoutes.SiteGroupsBreadcrumb.replace(':siteGroupId', id));
    return data;
  });

export const useGetSiteGroupsSearchAnalyticsMutation = (id: string) =>
  buildMutation<SiteGroupSearchAnalytics, QueryError, SearchConsoleAnalyticsRequestData>(
    [SiteGroupsQueryKeys.SearchAnalytics, id],
    async (postData) => {
      const { data } = await apiExecutor.post<SiteGroupSearchAnalytics>(
        ApiRoutes.SiteGroupsSearchAnalytics.replace(':siteGroupId', id),
        postData,
      );
      return data;
    },
  );

export const useAutocompleteSiteGroupsSitesQuery = (params: { query: string; includedIds?: string[] }) =>
  buildQuery<Pick<Site, 'id' | 'name'>[], QueryError>([SiteGroupsQueryKeys.SitesAutocomplete, params], async () => {
    const { data } = await apiExecutor.get<Pick<Site, 'id' | 'name'>[]>(ApiRoutes.SiteGroupsSitesAutocomplete, {
      params: { q: params.query, includedIds: params.includedIds?.length ? String(params.includedIds) : undefined },
    });
    return data;
  });
